import { useEffectOnValueChange } from '@timelyapp/react';
import classNames from 'classnames';
import OnboardingHintPopover from 'components/popovers/OnboardingHintPopover';
import Surface from 'components/Surface';
import {
  HAS_IGNORED_MEMORY,
  HAS_SEEN_AI_IGNORED_MEMORIES,
  HAS_SEEN_UNLINKED_MEMORIES,
} from 'constants/hints/onboarding';
import type { TimestampDragDropAction } from 'constants/timestamps-dnd';
import { DateTime } from 'luxon';
import React, { useCallback, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { toggleAppsListModal } from 'redux/modules/apps.actions';
import { getIsDayLockedByDate } from 'redux/modules/dayProperties.selectors';
import {
  createHourDraft,
  setHourDraftProject,
  setHourDraftTimestamp,
  setHourTimestamp,
  toggleHourTimestampFromMemory,
} from 'redux/modules/hours.actions';
import {
  deleteEntryIds,
  initializeMemory,
  interactWithAiIgnoredMemoriesHint,
  markEntryIdsAsNotSpam,
  markEntryIdsAsSpam,
  predictEntryIdsToProjectId,
  preserveScrollPosition,
  setFilter,
  toggleDisplayRuleForMode,
  toggleExpandEntryId,
  toggleExpandGroupRef,
  toggleSelectEntryIds,
} from 'redux/modules/timeline.actions';
import {
  handleDroppedMemory,
  toggleEntryIdsOnHourDraft,
  toggleEntryIdsOnPendingDraft,
} from 'redux/modules/timeline.memory.actions';
import { setGutterHighlight, setTimelineStyle } from 'redux/modules/timeline.types';
import { saveUserProperty, setTicMemoryViewmode } from 'redux/modules/user.actions';
import { getTimelineSettings } from 'redux/selectors/timeline';
import { getMemorySidebarProps } from 'redux/selectors/timeline.memories';
import type { IState } from 'redux/state';
import type { MappedActions } from 'types/redux';
import { OperatorMode } from 'types/timeline';
import { fromISO } from 'utils/luxon-helpers';
import { memorySidebarDateReference } from 'utils/timeline';
import { TIMELINE_OFFSET_TOP_PX } from '~domains/memory/timeline/constants/constants.timeline';
import AiIgnoredMemoriesHint from './AiIgnoredMemoriesHint';
import styles from './MemorySidebar.module.scss';
import MemorySidebarHeading from './MemorySidebarHeading';
import { MemoryInbox } from './memory';
import { MemoryGroups } from './memory/MemoryGroups';
import MemorySidebarDropzone from './memory/MemorySidebarDropzone';
import { MemoryTimeline } from './memory/MemoryTimeline';

interface ExternalProps {
  date: string;
  compact?: boolean;
  /** Indicates if MemorySidebar is inlined, needed to position Zoom controls */
  inline?: boolean;
  className?: string;
  pixelsPerMinute?: number;
}

type StateProps = ReturnType<typeof mapStateToProps>;

// See comments in timeline.memories.js
const mapStateToProps = (state: IState, ownProps: ExternalProps) => ({
  ...getMemorySidebarProps(state),
  isDayLocked: getIsDayLockedByDate(state, ownProps.date),
});

const mapActionsToProps = {
  toggleEntryIdsOnHourDraft,
  toggleEntryIdsOnPendingDraft,
  toggleExpandGroupRef,
  toggleExpandEntryId,
  toggleDisplayRuleForMode,
  toggleSelectEntryIds,
  toggleHourTimestampFromMemory,
  markEntryIdsAsSpam,
  markEntryIdsAsNotSpam,
  deleteEntryIds,
  predictEntryIdsToProjectId,
  setFilter,
  setTimelineStyle,
  setGutterHighlight,
  setHourTimestamp,
  setHourDraftTimestamp,
  initializeMemory,
  setHourDraftProject,
  createHourDraft,
  saveUserProperty,
  setTicMemoryViewmode,
  handleDroppedMemory,
  preserveScrollPosition,
  toggleAppsListModal,
  interactWithAiIgnoredMemoriesHint,
};

type ActionProps = MappedActions<typeof mapActionsToProps>;

type Props = ExternalProps & StateProps & ActionProps;

const MemorySidebar: React.FC<Props> = ({
  className,
  compact = false,
  date,
  deleteEntryIds,
  filter,
  groups,
  handleDroppedMemory,
  hasExperimentalStyle,
  hints,
  initializeMemory,
  inline = false,
  interactWithAiIgnoredMemoriesHint,
  isDayLocked,
  markEntryIdsAsNotSpam,
  markEntryIdsAsSpam,
  memoryInbox,
  meta,
  pixelsPerMinute,
  predictEntryIdsToProjectId,
  preserveScrollPosition,
  saveUserProperty,
  setFilter,
  setGutterHighlight,
  setHourDraftTimestamp,
  setHourTimestamp,
  setTimelineStyle,
  setTicMemoryViewmode,
  timeline,
  timelineStyle,
  toggleAppsListModal,
  toggleDisplayRuleForMode,
  toggleEntryIdsOnHourDraft,
  toggleHourTimestampFromMemory,
  toggleEntryIdsOnPendingDraft,
  toggleExpandEntryId,
  toggleExpandGroupRef,
  toolbarApps,
  ui,
  XhrCacheBuster,
}) => {
  useEffectOnValueChange(
    XhrCacheBuster,
    () => {
      initializeMemory({ date });
    },
    true
  );

  const handleToggleSelectEntryId = useCallback(
    (id: number) => {
      if (ui.hasHourDraft) {
        toggleEntryIdsOnHourDraft([id]);
      } else {
        toggleEntryIdsOnPendingDraft([id]);
      }
    },
    [toggleEntryIdsOnHourDraft, toggleEntryIdsOnPendingDraft, ui.hasHourDraft]
  );

  const handleToggleSelectEntryIds = useCallback(
    (entryIds: Array<number>) => {
      if (ui.hasHourDraft) {
        toggleEntryIdsOnHourDraft(entryIds);
      } else {
        toggleEntryIdsOnPendingDraft(entryIds);
      }
    },
    [toggleEntryIdsOnHourDraft, toggleEntryIdsOnPendingDraft, ui.hasHourDraft]
  );

  const handleDroppedTimestamp = useCallback(
    ({ timestamp, incrementInMinutes: minutes }: TimestampDragDropAction) => {
      if (timestamp.index == null) return;
      if (__DEVELOPMENT__) {
        console.warn('🪵 handleDroppedTimestamp', { timestamp });
      }
      const from = fromISO(timestamp.from).plus({ minutes }).toISO();
      const to = fromISO(timestamp.to).plus({ minutes }).toISO();

      if (timestamp.hourId) {
        setHourTimestamp(
          timestamp.hourId,
          timestamp.index,
          { from, to },
          { interaction: 'Timestamp Selection' }
        );
      } else {
        setHourDraftTimestamp(timestamp.index, { from, to });
      }
    },
    [setHourDraftTimestamp, setHourTimestamp]
  );

  const [inboxScrollPosition, setInboxScrollPosition] = useState(0);
  const [groupsScrollPosition, setGroupsScrollPosition] = useState(0);
  const { timestampSnappingMinutes } = useSelector(getTimelineSettings);

  const renderSidebarContent = () => {
    const wrapWithDropzone = (children: React.ReactElement) => (
      <MemorySidebarDropzone
        onDropMemory={handleDroppedMemory}
        onDropTimestamp={handleDroppedTimestamp}
        onDropTimestampPreview={setGutterHighlight}
        pixelsPerMinute={pixelsPerMinute}
        snapToMinutes={timestampSnappingMinutes}
        offsetTopPx={TIMELINE_OFFSET_TOP_PX}
      >
        {children}
      </MemorySidebarDropzone>
    );

    if (filter.mode === 'timeline') {
      return wrapWithDropzone(
        <MemoryTimeline
          ui={ui}
          compact={compact}
          pixelsPerMinute={pixelsPerMinute}
          snapToMinutes={timestampSnappingMinutes}
          offsetTopPx={TIMELINE_OFFSET_TOP_PX}
          timeFormat={filter.timeFormat}
          toggleSelectEntryId={handleToggleSelectEntryId}
          markEntryIdsAsSpam={markEntryIdsAsSpam}
          markEntryIdsAsNotSpam={markEntryIdsAsNotSpam}
          predictEntryIdsToProjectId={predictEntryIdsToProjectId}
          deleteEntryIds={deleteEntryIds}
          timeline={timeline}
          meta={meta}
          inline={inline}
          disabled={ui.isDisabled || isDayLocked}
          preserveScrollPosition={preserveScrollPosition}
          timelineStyle={timelineStyle}
          useProjectColorOnMemories={hasExperimentalStyle}
          isSubscriptionExpiredOrTrialEnded={ui.isSubscriptionExpiredOrTrialEnded}
        />
      );
    }

    if (ui.operatorMode === OperatorMode.memoryInbox) {
      return wrapWithDropzone(
        <MemoryInbox
          ui={ui}
          hasExperimentalStyle={hasExperimentalStyle}
          toggleHourTimestampFromMemory={toggleHourTimestampFromMemory}
          toggleSelectEntryIds={handleToggleSelectEntryIds}
          toggleExpandEntryId={toggleExpandEntryId}
          toggleExpandGroupRef={toggleExpandGroupRef}
          toggleDisplayRuleForMode={toggleDisplayRuleForMode}
          markEntryIdsAsSpam={markEntryIdsAsSpam}
          markEntryIdsAsNotSpam={markEntryIdsAsNotSpam}
          predictEntryIdsToProjectId={predictEntryIdsToProjectId}
          deleteEntryIds={deleteEntryIds}
          groups={memoryInbox}
          meta={meta}
          initialScrollPosition={groupsScrollPosition}
          setScrollPosition={setInboxScrollPosition}
          isSubscriptionExpiredOrTrialEnded={ui.isSubscriptionExpiredOrTrialEnded}
        />
      );
    }
    return wrapWithDropzone(
      <MemoryGroups
        setFilter={setFilter}
        filter={filter}
        ui={ui}
        hasExperimentalStyle={hasExperimentalStyle}
        toggleSelectEntryIds={handleToggleSelectEntryIds}
        toggleHourTimestampFromMemory={toggleHourTimestampFromMemory}
        toggleExpandEntryId={toggleExpandEntryId}
        toggleExpandGroupRef={toggleExpandGroupRef}
        toggleDisplayRuleForMode={toggleDisplayRuleForMode}
        markEntryIdsAsSpam={markEntryIdsAsSpam}
        markEntryIdsAsNotSpam={markEntryIdsAsNotSpam}
        predictEntryIdsToProjectId={predictEntryIdsToProjectId}
        deleteEntryIds={deleteEntryIds}
        groups={groups}
        meta={meta}
        disabled={ui.isDisabled || isDayLocked}
        initialScrollPosition={inboxScrollPosition}
        setScrollPosition={setGroupsScrollPosition}
      />
    );
  };

  const renderHintSidebar = () => {
    if (hints.includes(HAS_SEEN_UNLINKED_MEMORIES)) {
      return (
        <OnboardingHintPopover
          position="bottom"
          isOpen={hints.includes(HAS_SEEN_UNLINKED_MEMORIES)}
          target={renderSidebarContent()}
          onConfirm={() => saveUserProperty(HAS_SEEN_UNLINKED_MEMORIES)}
          hintKey={HAS_SEEN_UNLINKED_MEMORIES}
          ignoreTargetOnClick={false}
        />
      );
    }

    return (
      <>
        {renderSidebarContent()}
        {hints.includes(HAS_SEEN_AI_IGNORED_MEMORIES) && (
          <AiIgnoredMemoriesHint
            count={ui.aiIgnoredMemoriesCount}
            onClick={interactWithAiIgnoredMemoriesHint}
          />
        )}
      </>
    );
  };

  const containerClass = classNames(styles.root, className, {
    [styles.embeddedInModal]: !inline,
  });

  return (
    <Surface surface={2} className={containerClass}>
      <div>
        <MemorySidebarHeading
          title={memorySidebarDateReference(DateTime.now().toISODate(), date)}
          setFilter={setFilter}
          setTimelineStyle={setTimelineStyle}
          setTicMemoryViewmode={setTicMemoryViewmode}
          filter={filter}
          isExperimentalUser={ui.showDebugInfo}
          showEmptyState={ui.showEmptyState}
          isAppInitialized={ui.isAppInitialized}
          emptyStateType={ui.emptyStateType}
          displayRules={ui.displayRules}
          toggleDisplayRuleForMode={toggleDisplayRuleForMode}
          toggleAppsListModal={toggleAppsListModal}
          apps={toolbarApps}
          saveUserProperty={saveUserProperty}
          hideShortDurationMemoryTitles={ui.hideShortDurationMemoryTitles}
          hasEnabledTic={ui.hasEnabledTic}
          hasTicWorkspaceSupport={ui.hasTicWorkspaceSupport}
          showIgnoredMemoryHint={hints.includes(HAS_IGNORED_MEMORY)}
        />
      </div>
      {renderHintSidebar()}
    </Surface>
  );
};

export default connect(mapStateToProps, mapActionsToProps)(MemorySidebar);
